import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useForceDark } from "@/hooks/useForceDark";
import { RequestAccessDialog } from "@/components/auth/RequestAccessDialog";

const BG = "https://images.unsplash.com/photo-1704633785114-52104ce3d626?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHBpdGNoJTIwYWVyaWFsJTIwZ3Jhc3N8ZW58MHx8fHwxNzg3MDYxNjM2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  useForceDark();
  const { login, siteSettings } = useAuth();
  const allowRegister = !!siteSettings?.self_registration_enabled;
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const expired = sp.get("expired") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRequest, setShowRequest] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      if (u.must_change_password) nav("/app/change-password");
      else nav(u.role === "admin" ? "/app/admin" : u.role === "vendor" ? "/app/calendar" : u.role === "treasurer" ? "/app/finance" : "/app/pitches");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden">
        <img src={BG} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0F1C] via-[#0A0F1C]/80 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3" data-testid="login-logo">
            <img src="/logo.png" alt="Elsenham YFC" className="w-11 h-11 rounded-full object-contain" />
            <div className="font-display font-black text-lg leading-none tracking-tight">ELSENHAM YFC</div>
          </Link>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Sign in</div>
            <h1 className="font-display font-black text-5xl mt-3 leading-tight">Ready.<br/>Book.<br/>Play.</h1>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-start px-6 md:px-16 py-16">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          {expired && <div className="rounded-md border border-amber-400/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="login-expired-notice">Your session has expired, please sign in again.</div>}
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Welcome back</div>
            <h2 className="font-display font-black text-4xl mt-2">Sign in</h2>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="text-xs tracking-widest uppercase text-white/60">Email</Label>
              <Input id="email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} data-testid="login-email-input" className="mt-2 bg-[#111827] border-white/10 focus:border-[#83B68B] focus:ring-[#83B68B]/30" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-widest uppercase text-white/60">Password</Label>
              <Input id="password" type="password" required value={password} onChange={e=>setPassword(e.target.value)} data-testid="login-password-input" className="mt-2 bg-[#111827] border-white/10 focus:border-[#83B68B] focus:ring-[#83B68B]/30" />
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="login-submit-btn" className="btn-volt w-full py-3 rounded-md disabled:opacity-50">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-center">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs tracking-widest uppercase text-white/60 hover:text-[#83B68B] transition-colors">
              Forgot password?
            </Link>
          </div>
          <div className="text-sm text-white/60 text-center">
            {allowRegister ? (
              <>No account?{" "}
                <Link to="/register" className="text-[#83B68B] hover:underline" data-testid="login-to-register-link">Create one</Link>
              </>
            ) : (
              <div className="space-y-3">
                <div className="opacity-70">Accounts are created by club admins. Please message support if you require access.</div>
                <button type="button" onClick={() => setShowRequest(true)} data-testid="login-message-support-btn" className="text-[10px] tracking-widest uppercase px-4 py-2 rounded border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B]/10">Message support</button>
              </div>
            )}
          </div>
        </form>
        <RequestAccessDialog open={showRequest} onClose={() => setShowRequest(false)} />
      </div>
    </div>
  );
}
